import { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select, Text, TextField } from '@cencosud-cencommerce/paris-uikit';

import { noEmojiRegexExp, PHONE_PREFIX_OPTIONS } from '@/constants';
import {
  LABEL_CONTACT_INFO,
  LABEL_FIRST_NAME,
  LABEL_LAST_NAME,
  LABEL_PHONE,
  LABEL_PHONE_PREFIX
} from '@/sections/address/components/AddressForm/constants';
import {
  focusAndScroll,
  getInputStatus
} from '@/sections/address/components/AddressForm/utils';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';

interface Props {
  isDniEnabled?: boolean;
}

export const CustomerInformation = ({ isDniEnabled = false }: Props) => {
  const { control, formState, resetField } =
    useFormContext<AddressFormValues>();

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (formState.errors.firstName) {
      focusAndScroll(firstNameRef);
    } else if (formState.errors.lastName) {
      focusAndScroll(lastNameRef);
    } else if (formState.errors.phone) {
      focusAndScroll(phoneRef);
    }
  }, [formState.errors]);

  return (
    <div className="co-flex co-gap-6 co-flex-col">
      <Text
        className="co-text-[14px] co-leading-[20px] co-text-[#363F45]"
        weight={600}
        variant="body1"
      >
        {LABEL_CONTACT_INFO}
      </Text>

      <Controller
        control={control}
        name="firstName"
        render={({ field, fieldState, formState }) => (
          <TextField
            {...field}
            ref={firstNameRef}
            label={LABEL_FIRST_NAME}
            helperText={fieldState.error?.message}
            status={getInputStatus(formState, fieldState)}
            onChange={(e) => {
              const value = e.target.value.replace(noEmojiRegexExp, '');
              field.onChange(value);
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="lastName"
        render={({ field, fieldState, formState }) => (
          <TextField
            {...field}
            ref={lastNameRef}
            label={LABEL_LAST_NAME}
            helperText={fieldState.error?.message}
            status={getInputStatus(formState, fieldState)}
            onChange={(e) => {
              const value = e.target.value.replace(noEmojiRegexExp, '');
              field.onChange(value);
            }}
          />
        )}
      />

      <div className="co-flex co-flex-col co-gap-6 tablet_w:co-flex-row tablet_w:co-gap-2">
        <div className="co-w-full co-min-w-[160px] tablet_w:co-w-1/3">
          <Controller
            control={control}
            name="prefix-phone"
            render={({ field, fieldState, formState }) => (
              <Select
                {...field}
                options={PHONE_PREFIX_OPTIONS}
                label={LABEL_PHONE_PREFIX}
                disabled={!isDniEnabled}
                helperText={fieldState.error?.message}
                status={getInputStatus(formState, fieldState)}
                onChange={(e) => {
                  const fieldValue = e.target.value;
                  field.onChange(fieldValue);
                  resetField('phone', { defaultValue: '' });
                }}
              />
            )}
          />
        </div>

        <div className="co-w-full tablet_w:co-w-2/3">
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                ref={phoneRef}
                label={LABEL_PHONE}
                type="tel"
                helperText={fieldState.error?.message}
                status={getInputStatus(formState, fieldState)}
                onChange={(e) => {
                  const value = e.target.value.replace(noEmojiRegexExp, '');
                  field.onChange(value);
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

CustomerInformation.displayName = 'CustomerInformation';

export default CustomerInformation;
